import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AppLayout } from "../shared/layout";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { CardGridLoader } from "@/components/shared/page-loader";
import { getTask, updateTask, deleteTask } from "@/data/requests/task-requests";
import { format, parseISO } from "date-fns";
import {
  ArrowLeft,
  CheckCircle2,
  Circle,
  Zap,
  Star,
  Calendar,
  Trash2,
  Award,
} from "lucide-react";

export function TaskDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: taskResponse, isLoading, isError } = useQuery({
    queryKey: ["task", id],
    queryFn: () => getTask(id),
    enabled: !!id,
  });

  const task = taskResponse?.data;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isUrgent, setIsUrgent] = useState(false);
  const [isImportant, setIsImportant] = useState(false);
  const [xpReward, setXpReward] = useState("10");
  const [dueDate, setDueDate] = useState("");

  useEffect(() => {
    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      setIsUrgent(!!task.isUrgent);
      setIsImportant(!!task.isImportant);
      setXpReward(
        typeof task.xpReward === "number"
          ? String(task.xpReward)
          : task.xpReward || "10"
      );
      setDueDate(
        task.dueDate
          ? format(
              typeof task.dueDate === "string"
                ? parseISO(task.dueDate)
                : new Date(task.dueDate),
              "yyyy-MM-dd'T'HH:mm"
            )
          : ""
      );
    }
  }, [task]);

  const updateMutation = useMutation({
    mutationFn: (data) => updateTask(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task", id] });
      toast({
        title: t("taskDetail.updatedTitle"),
        description: t("taskDetail.updatedDescription"),
      });
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description:
          error.response?.data?.message || t("taskDetail.updateFailed"),
        variant: "destructive",
      });
    },
  });

  const toggleCompleteMutation = useMutation({
    mutationFn: (isCompleted) => updateTask(id, { isCompleted }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      queryClient.invalidateQueries({ queryKey: ["task", id] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks"] });
      toast({
        title: t("taskDetail.deletedTitle"),
        description: t("taskDetail.deletedDescription"),
      });
      navigate("/plan");
    },
    onError: (error) => {
      toast({
        title: t("common.error"),
        description:
          error.response?.data?.message || t("taskDetail.deleteFailed"),
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    if (!title.trim()) {
      toast({
        title: t("taskDetail.validation.titleRequiredTitle"),
        description: t("taskDetail.validation.titleRequiredMessage"),
        variant: "destructive",
      });
      return;
    }

    const payload = {
      title,
      description,
      isUrgent,
      isImportant,
      xpReward: parseInt(xpReward, 10) || 10,
      dueDate: dueDate ? new Date(dueDate).toISOString() : null,
    };

    updateMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <AppLayout title={t("taskDetail.title")} description={t("common.loading")}>
        <CardGridLoader count={2} />
      </AppLayout>
    );
  }

  if (isError || !task) {
    return (
      <AppLayout title={t("taskDetail.title")}>
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle>{t("taskDetail.notFound")}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("common.back")}
            </Button>
          </CardContent>
        </Card>
      </AppLayout>
    );
  }

  const accentColor =
    task.isUrgent && task.isImportant
      ? "text-red-500"
      : task.isImportant
      ? "text-blue-500"
      : task.isUrgent
      ? "text-amber-500"
      : "text-emerald-500";

  return (
    <AppLayout
      title={t("taskDetail.title")}
      description={t("taskDetail.subtitle")}
      actions={[
        {
          name: t("common.back"),
          icon: <ArrowLeft className="h-4 w-4" />,
          onClick: () => navigate(-1),
        },
      ]}
    >
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-2">
                <CardTitle className="flex items-center gap-2">
                  {task.isCompleted ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <Circle className={`h-5 w-5 ${accentColor}`} />
                  )}
                  {title || t("taskDetail.titlePlaceholder")}
                </CardTitle>
                <CardDescription>
                  {t("taskDetail.descriptionHelper")}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    if (
                      window.confirm(t("taskDetail.delete.confirmMessage"))
                    ) {
                      deleteMutation.mutate();
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("taskDetail.fields.titleLabel")}
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("taskDetail.fields.titlePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("taskDetail.fields.descriptionLabel")}
              </label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("taskDetail.fields.descriptionPlaceholder")}
                rows={4}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {t("taskDetail.fields.dueDate")}
                </label>
                <Input
                  type="datetime-local"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  {t("taskDetail.fields.reward")}
                </label>
                <Input
                  type="number"
                  value={xpReward}
                  onChange={(e) => setXpReward(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={isUrgent ? "default" : "outline"}
                onClick={() => setIsUrgent((v) => !v)}
                className="flex items-center gap-2"
              >
                <Zap className="h-4 w-4" />
                {t("taskDetail.priority.urgent")}
              </Button>
              <Button
                type="button"
                variant={isImportant ? "default" : "outline"}
                onClick={() => setIsImportant((v) => !v)}
                className="flex items-center gap-2"
              >
                <Star className="h-4 w-4" />
                {t("taskDetail.priority.important")}
              </Button>
            </div>

            <div className="flex items-center justify-between pt-2 border-t">
              <div className="space-y-1 text-xs text-muted-foreground">
                {task.createdAt && (
                  <div>
                    {t("taskDetail.meta.created", {
                      date: format(
                        typeof task.createdAt === "string"
                          ? parseISO(task.createdAt)
                          : new Date(task.createdAt),
                        "dd.MM.yyyy HH:mm"
                      ),
                    })}
                  </div>
                )}
                {task.updatedAt && (
                  <div>
                    {t("taskDetail.meta.updated", {
                      date: format(
                        typeof task.updatedAt === "string"
                          ? parseISO(task.updatedAt)
                          : new Date(task.updatedAt),
                        "dd.MM.yyyy HH:mm"
                      ),
                    })}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant={task.isCompleted ? "outline" : "default"}
                  onClick={() =>
                    toggleCompleteMutation.mutate(!task.isCompleted)
                  }
                  className="flex items-center gap-2"
                >
                  {task.isCompleted ? (
                    <>
                      <Circle className="h-4 w-4" />
                      {t("taskDetail.actions.markIncomplete")}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      {t("taskDetail.actions.complete")}
                    </>
                  )}
                </Button>
                <Button onClick={handleSave} disabled={updateMutation.isPending}>
                  {updateMutation.isPending
                    ? t("common.saving")
                    : t("common.save")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {task.tags && task.tags.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                {t("taskDetail.tags.title")}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-2">
              {task.tags.map((tag) => (
                <Badge key={tag} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}




